import { useEffect, useState } from "react";

import { orderBy } from "lodash";

import { cn, Drawer, Select, Typography } from "@eisox/design-system";
import { createContext } from "@radix-ui/react-context";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

/* -------------------------------------------------------------------------------------------------
 * ItemDrawer
 * -----------------------------------------------------------------------------------------------*/

const ITEM_DRAWER_NAME = "ItemDrawer";

interface WithId {
  id: string;
  uid: number;
  errors: unknown[];
  warnings: unknown[];
}

interface ItemDrawerContextValue<T extends WithId> {
  selectedItemId: string;
  setSelectedItemId: (id: string) => void;
  items: T[];
  open: boolean;
  setOpen: (open: boolean) => void;
}

const [ItemDrawerProvider, useItemDrawerContext] = createContext<ItemDrawerContextValue<WithId>>(ITEM_DRAWER_NAME);

interface ItemDrawerProps<T extends WithId> extends Drawer.DrawerProps {
  items: T[];
  defaultSelectedItemId?: string;
}

const Root = <T extends WithId>({
  open: openProp,
  defaultOpen = false,
  // oxlint-disable-next-line typescript/unbound-method
  onOpenChange,
  items,
  defaultSelectedItemId,
  ...props
}: ItemDrawerProps<T>) => {
  const itemsSortedByUid = orderBy(items, "uid", "asc");

  const [selectedItemId, setSelectedItemId] = useState<string | undefined>(
    defaultSelectedItemId ?? itemsSortedByUid[0]?.id,
  );

  const [open, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange,
  });

  const handleOpenChange = (open: boolean) => {
    setSelectedItemId(defaultSelectedItemId ?? itemsSortedByUid[0]?.id);
    setOpen(open);
  };

  if (!selectedItemId) return null;

  return (
    <ItemDrawerProvider
      selectedItemId={selectedItemId}
      setSelectedItemId={setSelectedItemId}
      items={itemsSortedByUid}
      open={open}
      setOpen={setOpen}
    >
      <Drawer.Root {...props} open={open} onOpenChange={handleOpenChange} />
    </ItemDrawerProvider>
  );
};

Root.displayName = ITEM_DRAWER_NAME;

/* -------------------------------------------------------------------------------------------------
 * Trigger
 * -----------------------------------------------------------------------------------------------*/

type TriggerProps = Drawer.TriggerProps;

const Trigger = Drawer.Trigger;

Trigger.displayName = "ItemDrawerTrigger";

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/

const CONTENT_NAME = "ItemDrawerContent";

interface ContentProps<T extends WithId> extends Omit<Drawer.ContentProps, "children"> {
  title: string;
  select: {
    title: Paths<T>;
    subtitle: Paths<T>;
  };
  children: (selectedItem: T) => React.ReactNode;
}

const Content = <T extends WithId>({ title, select, className, children, ...props }: ContentProps<T>) => {
  const { selectedItemId, setSelectedItemId, items, setOpen } = useItemDrawerContext(CONTENT_NAME);

  useEffect(() => {
    const selectedItem = items.find(item => item.id === selectedItemId);
    if (!selectedItem) {
      if (items.length > 0) {
        const firstItem = items[0];
        if (firstItem) setSelectedItemId(firstItem.id);
      } else {
        setOpen(false);
      }
    }
  }, [items, selectedItemId, setSelectedItemId, setOpen]);

  const selectedItem = items.find(item => item.id === selectedItemId);
  const selectedItemTyped = selectedItem as T | undefined;

  return (
    <Drawer.Content {...props}>
      <Drawer.Header>{title}</Drawer.Header>
      <div className="md:gap-[20px] md:p-[40px] flex h-full flex-col gap-[10px] overflow-y-auto p-[10px]">
        <Select.Root multiple={false} value={selectedItemId} onValueChange={setSelectedItemId}>
          <Select.Trigger>
            <Select.Value className="flex items-center gap-[10px] [&_span]:text-primary-dark-gray">
              {selectedItemTyped && (
                <>
                  <Typography as="span" disableTooltipOnTouch>
                    {/* oxlint-disable-next-line typescript/no-unnecessary-condition -- runtime API guard despite OpenAPI typing */}
                    {`${selectedItemTyped.uid} - ${String(selectedItemTyped[select.title] ?? "-")}`}{" "}
                    {/* oxlint-disable-next-line typescript/no-unnecessary-condition -- runtime API guard despite OpenAPI typing */}
                    <span>{`(${String(selectedItemTyped[select.subtitle] ?? "-")})`}</span>
                  </Typography>
                  {selectedItemTyped.errors.length > 0 && (
                    <span className="h-[10px] w-[10px] shrink-0 rounded-full bg-primary-red" />
                  )}
                  {selectedItemTyped.warnings.length > 0 && selectedItemTyped.errors.length === 0 && (
                    <span className="h-[10px] w-[10px] shrink-0 rounded-full bg-primary-orange" />
                  )}
                </>
              )}
            </Select.Value>
          </Select.Trigger>
          <Select.Content>
            {items.length > 10 && <Select.Input />}
            <Select.Viewport>
              {(items as T[]).map(item => (
                <Select.Item
                  key={item.id}
                  value={item.id}
                  className="flex items-center gap-[10px] [&_span]:text-primary-dark-gray"
                >
                  <Typography disableTooltipOnTouch>
                    {/* oxlint-disable-next-line typescript/no-unnecessary-condition -- runtime API guard despite OpenAPI typing */}
                    {`${item.uid} - ${String(item[select.title] ?? "-")}`}{" "}
                    {/* oxlint-disable-next-line typescript/no-unnecessary-condition -- runtime API guard despite OpenAPI typing */}
                    <span>{`(${String(item[select.subtitle] ?? "-")})`}</span>
                  </Typography>
                  {item.errors.length > 0 && (
                    <span className="h-[10px] w-[10px] shrink-0 rounded-full bg-primary-red" />
                  )}
                  {item.warnings.length > 0 && item.errors.length === 0 && (
                    <span className="h-[10px] w-[10px] shrink-0 rounded-full bg-primary-orange" />
                  )}
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Root>
        <div
          className={cn(
            `min-h-0 md:p-[20px] flex flex-1 flex-col gap-[20px] overflow-y-auto rounded-[10px] bg-primary-gray p-[10px]`,
            className,
          )}
        >
          {selectedItemTyped && children(selectedItemTyped)}
        </div>
      </div>
    </Drawer.Content>
  );
};

Content.displayName = CONTENT_NAME;

export { Content, Root, Trigger };
export type { ContentProps, ItemDrawerProps, TriggerProps };
